/**
 * PrizeClaimFlow — the three-step claim overlay from Figma (9169:57887 /
 * 57980 / 58074): confirm ("You are claiming …"), success
 * ("Congratulations!"), and the insufficient-points stop screen.
 *
 * Chromeless centered card (the design has no modal header), following the
 * house overlay conventions: backdrop click + Escape close, role="dialog",
 * scaleIn motion, focus ring on actions.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { scaleIn, DURATION_NORMAL, EASE_OUT } from '@/lib/motion';
import { useImageUrl } from '../../services/uploads';
import { useClaimPrize } from '../../hooks/learner/usePrizes';

/** Structural subset shared by list rows and the detail payload. */
interface ClaimablePrize {
  id: string;
  title: string;
  price_points: number;
  media_urls: string[];
}

type Step = 'confirm' | 'success' | 'insufficient';

interface PrizeClaimFlowProps {
  prize: ClaimablePrize;
  /** Current points balance — decides confirm vs insufficient. */
  balance: number;
  isOpen: boolean;
  onClose: () => void;
}

function PrizeImage({ prize }: { prize: ClaimablePrize }) {
  const [url] = useImageUrl(prize.media_urls[0] ?? null);
  return url ? (
    <img
      src={url}
      alt=""
      className="w-40 h-40 object-contain select-none"
      draggable={false}
    />
  ) : (
    <div
      aria-hidden="true"
      className="w-40 h-40 rounded-2xl"
      style={{
        background: 'linear-gradient(135deg, rgb(var(--color-primary) / 0.18), rgb(var(--color-secondary) / 0.18))',
      }}
    />
  );
}

export default function PrizeClaimFlow({
  prize,
  balance,
  isOpen,
  onClose,
}: PrizeClaimFlowProps) {
  const claim = useClaimPrize();
  const [step, setStep] = useState<Step>('confirm');

  // Pick the entry step ONLY on the closed→open transition. A successful
  // claim immediately drops the live balance, and re-evaluating on that
  // change would stomp the success screen with the insufficient one.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setStep(balance >= prize.price_points ? 'confirm' : 'insufficient');
    }
    wasOpen.current = isOpen;
  }, [isOpen, balance, prize.price_points]);

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, [isOpen, onClose]);

  const handleClaim = async () => {
    try {
      await claim.mutateAsync(prize.id);
      setStep('success');
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { error?: string } } })?.response?.data
        ?.error;
      if (code === 'INSUFFICIENT_POINTS') {
        setStep('insufficient');
      } else {
        onClose();
        const { default: toast } = await import('react-hot-toast');
        toast.error('Could not claim the prize. Please try again.');
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !claim.isPending) onClose();
          }}
          data-testid="prize-claim-flow"
        >
          <motion.div
            {...scaleIn}
            role="dialog"
            aria-modal="true"
            aria-label={
              step === 'success'
                ? 'Prize claimed'
                : step === 'insufficient'
                  ? 'Not enough points'
                  : `Claim ${prize.title}`
            }
            className="w-full max-w-[340px] rounded-2xl bg-surface-elevated shadow-2xl px-6 pb-6 pt-4 flex flex-col items-center text-center gap-2"
            style={{ willChange: 'transform, opacity' }}
          >
            <PrizeImage prize={prize} />

            {step === 'confirm' && (
              <>
                <h2 className="text-lg font-bold text-text-primary leading-snug">
                  You are claiming
                  <br />
                  {prize.title}
                </h2>
                <p className="text-sm text-text-secondary">
                  If you claim this prize you will be giving up{' '}
                  {prize.price_points.toLocaleString()} points.
                </p>
                <div className="mt-3 flex w-full items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClaim}
                    disabled={claim.isPending}
                    className="flex-1 h-10 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {claim.isPending ? 'Claiming…' : 'Claim Prize'}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={claim.isPending}
                    className="h-10 px-4 rounded-full border border-border-primary text-sm font-bold text-text-primary hover:bg-text-primary/[0.04] focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}

            {step === 'success' && (
              <>
                <h2 className="text-lg font-bold text-success-500">Congratulations!</h2>
                <p className="text-sm text-text-secondary">
                  Your claim is in! Your trainer will confirm it and get the prize to you —
                  you can follow it in your Prize History.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full h-10 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold focus-ring active:scale-[0.97] transition-[background-color,transform] duration-fast ease-out-strong"
                >
                  Go back
                </button>
              </>
            )}

            {step === 'insufficient' && (
              <>
                <h2 className="text-lg font-bold text-text-primary leading-snug">
                  You do not have enough points to claim this prize at the moment
                </h2>
                <p className="text-sm text-text-secondary">Try again later!</p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full h-10 rounded-full bg-text-primary text-surface-elevated text-sm font-bold focus-ring active:scale-[0.97] transition-[opacity,transform] duration-fast ease-out-strong hover:opacity-90"
                >
                  Go back
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
